import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, MessageCircle, Mail, MapPin, Clock, Send, User, Car } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { mockCompanyInfo } from '../mock';

const Contact = () => {
  const { toast } = useToast();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true); // Start visible
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', subject: '', message: '', preferredContact: 'phone'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), { threshold: 0.1 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed');

      const whatsappMessage = `שלום, אני ${formData.name}\nנושא: ${formData.subject}\nהודעה: ${formData.message}\nטלפון: ${formData.phone}`;
      const phoneNumber = mockCompanyInfo.whatsapp.replace(/[^0-9]/g, '');
      window.open(`https://wa.me/972${phoneNumber.substring(1)}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');

      toast({ title: "הודעה נשלחה בהצלחה!", description: "נחזור אליך בהקדם" });
      setFormData({ name: '', phone: '', email: '', subject: '', message: '', preferredContact: 'phone' });
    } catch {
      toast({ title: "שגיאה בשליחה", description: "אנא נסה שוב", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const subjects = ["רכישת רכב", "מכירת רכב", "החלפת רכב (טרייד-אין)", "שירותי טיפוח", "ייעוץ כללי", "אחר"];

  return (
    <section id="contact" ref={sectionRef} className="py-24 bg-luxury-surface relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-luxury-gold/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-12 bg-luxury-gold" />
            <span className="text-luxury-gold text-sm font-medium tracking-[0.3em] uppercase">נשמח לשמוע ממך</span>
            <div className="h-px w-12 bg-luxury-gold" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-luxury-text mb-6">
            צור <span className="gold-shimmer">קשר</span>
          </h2>
          <p className="text-lg text-luxury-muted max-w-2xl mx-auto">
            מוכנים לעזור לכם למצוא את רכב החלומות
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <motion.div 
            className="lg:col-span-2"
            initial={{ opacity: 0, x: -30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Card className="bg-luxury-black border-luxury-border">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-luxury-text text-center">שלח לנו הודעה</CardTitle>
                <p className="text-luxury-muted text-center text-sm">מלא את הפרטים ונחזור אליך בהקדם</p>
              </CardHeader>
              
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-luxury-text text-sm">שם מלא *</Label>
                      <div className="relative">
                        <User className="absolute right-3 top-3 h-4 w-4 text-luxury-muted" />
                        <Input
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          required
                          className="pr-10 bg-luxury-surface border-luxury-border text-luxury-text placeholder-luxury-muted focus:border-luxury-gold rounded-none h-12"
                          placeholder="הכנס שם מלא"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-luxury-text text-sm">טלפון *</Label>
                      <div className="relative">
                        <Phone className="absolute right-3 top-3 h-4 w-4 text-luxury-muted" />
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          required
                          className="pr-10 bg-luxury-surface border-luxury-border text-luxury-text placeholder-luxury-muted focus:border-luxury-gold rounded-none h-12"
                          placeholder="050-0000000"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-luxury-text text-sm">אימייל</Label>
                      <div className="relative">
                        <Mail className="absolute right-3 top-3 h-4 w-4 text-luxury-muted" />
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="pr-10 bg-luxury-surface border-luxury-border text-luxury-text placeholder-luxury-muted focus:border-luxury-gold rounded-none h-12"
                          placeholder="email@example.com"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-luxury-text text-sm">נושא *</Label>
                      <div className="relative">
                        <Car className="absolute right-3 top-3 h-4 w-4 text-luxury-muted z-10" />
                        <Select value={formData.subject} onValueChange={(v) => handleInputChange('subject', v)}>
                          <SelectTrigger className="pr-10 bg-luxury-surface border-luxury-border text-luxury-text focus:border-luxury-gold rounded-none h-12">
                            <SelectValue placeholder="בחר נושא" />
                          </SelectTrigger>
                          <SelectContent className="bg-luxury-surface border-luxury-border rounded-none">
                            {subjects.map(s => <SelectItem key={s} value={s} className="text-luxury-text">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-luxury-text text-sm">הודעה *</Label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      required
                      rows={4}
                      className="bg-luxury-surface border-luxury-border text-luxury-text placeholder-luxury-muted focus:border-luxury-gold resize-none rounded-none"
                      placeholder="איך נוכל לעזור לך?"
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-luxury w-full flex items-center justify-center gap-2 disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isSubmitting ? (
                      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-5 h-5 border-2 border-luxury-black border-t-transparent rounded-full" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>שלח הודעה</span>
                      </>
                    )}
                  </motion.button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Info */}
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, x: 30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {/* Quick Contact */}
            <div className="glass border border-luxury-border p-6">
              <h3 className="text-xl font-bold text-luxury-text mb-6">יצירת קשר מהירה</h3>
              <div className="space-y-4">
                <motion.button
                  onClick={() => window.open(`tel:${mockCompanyInfo.phone}`, '_self')}
                  className="w-full flex items-center gap-4 p-4 bg-luxury-surface-light border border-luxury-border hover:border-luxury-gold transition-all duration-300 text-right"
                  whileHover={{ x: -5 }}
                >
                  <div className="w-12 h-12 bg-luxury-gold flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-luxury-black" />
                  </div>
                  <div>
                    <p className="text-luxury-muted text-sm">טלפון</p>
                    <p className="text-luxury-text font-medium">{mockCompanyInfo.phone}</p>
                  </div>
                </motion.button>

                <motion.button
                  onClick={() => {
                    const phoneNumber = mockCompanyInfo.whatsapp.replace(/[^0-9]/g, '');
                    window.open(`https://wa.me/972${phoneNumber.substring(1)}`, '_blank');
                  }}
                  className="w-full flex items-center gap-4 p-4 bg-green-600/20 border border-green-600/50 hover:bg-green-600/30 transition-all duration-300 text-right"
                  whileHover={{ x: -5 }}
                >
                  <div className="w-12 h-12 bg-green-600 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-luxury-muted text-sm">וואטסאפ</p>
                    <p className="text-luxury-text font-medium">{mockCompanyInfo.whatsapp}</p>
                  </div>
                </motion.button>

                <motion.button
                  onClick={() => window.location.href = `mailto:${mockCompanyInfo.email}`}
                  className="w-full flex items-center gap-4 p-4 bg-luxury-surface-light border border-luxury-border hover:border-luxury-gold transition-all duration-300 text-right"
                  whileHover={{ x: -5 }}
                >
                  <div className="w-12 h-12 border border-luxury-gold flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-luxury-gold" />
                  </div>
                  <div>
                    <p className="text-luxury-muted text-sm">אימייל</p>
                    <p className="text-luxury-text font-medium text-sm">{mockCompanyInfo.email}</p>
                  </div>
                </motion.button>
              </div>
            </div>

            {/* Info Cards */}
            <div className="glass border border-luxury-border p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-luxury-gold/50 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-luxury-gold" />
                </div>
                <div>
                  <p className="text-luxury-text font-medium mb-1">כתובת</p>
                  <p className="text-luxury-muted text-sm">{mockCompanyInfo.address}</p>
                </div>
              </div>
            </div>

            <div className="glass border border-luxury-border p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-luxury-gold/50 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-luxury-gold" />
                </div>
                <div>
                  <p className="text-luxury-text font-medium mb-1">שעות פעילות</p>
                  <p className="text-luxury-muted text-sm">{mockCompanyInfo.workingHours}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
